/**
 * Text oznámení v jazyce toho, kdo se dívá.
 *
 * Oznámení se ukládá do databáze jako hotová věta, takže jazyk se dosud
 * vybíral v okamžiku ZÁPISU: „Záloha odmítnuta — disk téměř plný" vznikla
 * česky a česky zůstala i pro toho, kdo má rozhraní v italštině. Přepnutí
 * jazyka s tím nemohlo nic udělat — ta věta už je uložená.
 *
 * Zapisovatel proto vedle věty ukládá i klíč do `metadata.i18n` a jazyk se
 * vybírá až tady, při čtení. Uložená věta zůstává fallbackem: historické
 * záznamy a hlášky, které klíč ještě nemají, se renderují přesně jako dřív.
 */
export interface NotificationI18nMeta {
  titleKey?: string;
  titleParams?: Record<string, string | number>;
  detailKey?: string;
  detailParams?: Record<string, string | number>;
}

export interface TranslatableNotification {
  title: string;
  detail?: string | null;
  metadata?: Record<string, unknown> | null;
}

/**
 * Překladač. Záměrně jen to, co odsud voláme — `t(klíč, fallback, parametry)`.
 * Vlastní typ `TFunction` z i18nextu se sem nedá napsat: je generický přes
 * jmenné prostory a klíče, takže by tenhle modul přitáhl celý překladový
 * strom a nešel by testovat bez react-i18next (ten Deno neumí resolvovat).
 */
// Návratový typ je `unknown`, ne `string`: i18nextí `TFunction` vrací podle
// nastavení i objekt nebo null, takže by se sem jinak nedala předat vůbec.
// Fallback je POVINNÝ: i18nextí `TFunction` nemá přetížení s jediným
// argumentem, takže volitelný druhý parametr by tenhle typ učinil
// nepřiřaditelným — a `t` by se sem nedalo předat.
// deno-lint-ignore no-explicit-any
export type Translate = (key: string, fallback: string, options?: any) => unknown;

function asText(value: unknown): string {
  return typeof value === 'string' ? value : value == null ? '' : String(value);
}

export function notificationI18n(
  metadata: Record<string, unknown> | null | undefined,
): NotificationI18nMeta | null {
  const raw = (metadata as { i18n?: unknown } | null | undefined)?.i18n;
  if (!raw || typeof raw !== 'object') return null;
  const meta = raw as NotificationI18nMeta;
  const usable = typeof meta.titleKey === 'string' || typeof meta.detailKey === 'string';
  return usable ? meta : null;
}

/**
 * Jeden text: klíč vyhrává, uložená věta je fallback.
 *
 * Chybějící překlad se nesmí projevit prázdným řádkem — proto se uložená věta
 * předává i jako fallback samotnému překladači. Klíč, který v lokále není,
 * tedy vrátí původní text, ne své vlastní jméno.
 */
function render(
  t: Translate,
  key: string | undefined,
  params: Record<string, string | number> | undefined,
  stored: string | null | undefined,
): string | null {
  if (!key) return stored ?? null;
  const translated = asText(t(key, stored ?? key, { ...(params ?? {}) }));
  return translated || stored || null;
}

/** Titulek a detail oznámení tak, jak je má číst tenhle uživatel. */
export function notificationText(
  notification: TranslatableNotification,
  t: Translate,
  opts: {
    /**
     * Uložená věta → klíč, pro záznamy bez `metadata.i18n`. Tím se dají
     * přeložit i zachycené toasty, které klíč nikdy nenesly.
     */
    sourceKey?: (text: string) => string | null;
  } = {},
): { title: string; detail: string | null } {
  const meta = notificationI18n(notification.metadata);
  if (!meta) {
    // Bez klíče se zkusí najít podle uloženého textu. Nenajde-li se, zůstává
    // text tak, jak byl uložen — nikdy se nevrací jméno klíče.
    const byText = opts.sourceKey;
    if (!byText) {
      return { title: notification.title, detail: notification.detail ?? null };
    }
    return {
      title: render(t, byText(notification.title) ?? undefined, undefined, notification.title) ??
        notification.title,
      detail: notification.detail
        ? render(t, byText(notification.detail) ?? undefined, undefined, notification.detail)
        : null,
    };
  }
  return {
    title: render(t, meta.titleKey, meta.titleParams, notification.title) ?? notification.title,
    detail: render(t, meta.detailKey, meta.detailParams, notification.detail),
  };
}
